import type { EmailDraftView, MailMessageView, MailThreadDetail } from '@fos/shared/contracts/mail';
import { MAIL_MESSAGES, MAIL_SEND_CHECK_TEXTS } from '@fos/shared/contracts/mail';
import {
  CLIENT_MATCH_BANNERS,
  CLIENT_MATCH_QUIET,
  EMAIL_DRAFT_APPROVAL_LABELS,
  EMAIL_DRAFT_STATUS_LABELS,
  EMAIL_DRAFT_STATUS_TONES,
  MAIL_IMPORTANCE_LABELS,
} from '@fos/shared/labels';
import { ChevronDown, Lock, Mail, Paperclip, Reply, Sparkles, UserRound, Users } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { Link } from 'react-router';
import { appErrorCode, errorMessage } from '../../../lib/api';
import { cn } from '../../../lib/cn';
import { formatRelativePast } from '../../../lib/format';
import { trpc } from '../../../lib/trpc';
import { Button } from '../../../ui/Button';
import { EmptyState, ErrorState, Skeleton } from '../../../ui/States';
import { Status } from '../../../ui/Status';
import { StatusPill } from '../../../ui/StatusPill';
import { toast } from '../../../ui/Toast';
import { TOUCH_BUTTON } from '../../mobile';
import { useMailRefresh } from '../hooks';
import { addressesText, addressShort } from '../mail-format';
import { getThreadActions } from '../slots';
import { AssignClientDialog, CreateClientFromSenderDialog } from './AssignClientDialog';
import { Composer } from './Composer';
import { MailboxBanner } from './MailboxStatus';

function ThreadSkeleton() {
  return (
    <div role="status" aria-busy="true" aria-label="Wczytywanie wątku" className="flex flex-col gap-3">
      <Skeleton className="h-8 w-2/3" />
      <Skeleton className="h-4 w-1/3" />
      <Skeleton className="mt-6 h-4 w-full" />
      <Skeleton className="h-4 w-11/12" />
      <Skeleton className="h-4 w-3/4" />
    </div>
  );
}

/** Odebrana: „Pilne” albo „Mniej ważne” (istotna — bez oznaczenia, kolor tylko gdy niesie znaczenie), „Do przeglądu”. */
function ClassificationStatus({ message }: { message: MailMessageView }) {
  if (message.direction !== 'in') return null;
  const importance = message.importance;
  return (
    <>
      {importance === 'critical' && <Status tone="danger">{MAIL_IMPORTANCE_LABELS.critical}</Status>}
      {importance === 'less' && <Status tone="neutral">{MAIL_IMPORTANCE_LABELS.less}</Status>}
      {message.classification?.needsReview && <Status tone="warning">Do przeglądu</Status>}
    </>
  );
}

function Attachments({ message }: { message: MailMessageView }) {
  if (message.attachments.length === 0) return null;
  return (
    <ul aria-label="Załączniki" className="mt-4 flex flex-col gap-1.5">
      {message.attachments.map((a) => (
        <li
          key={a.fileId ?? a.externalId ?? `${a.filename}:${a.sizeBytes}`}
          className="flex min-w-0 items-start gap-2 text-body-s"
        >
          <Paperclip size={16} strokeWidth={1.75} aria-hidden className="mt-0.5 shrink-0 text-fg-muted" />
          {a.downloadPath ? (
            <a
              href={a.downloadPath}
              className="min-w-0 break-all rounded-xs text-fg-secondary underline-offset-2 hover:text-fg hover:underline focus-visible:outline-2 focus-visible:outline-accent"
            >
              {a.filename}
            </a>
          ) : (
            <span className="min-w-0 break-words text-fg-muted">
              {a.filename} — pominięty{a.skipReason ? `: ${a.skipReason}` : ''}
            </span>
          )}
        </li>
      ))}
    </ul>
  );
}

/** Jedna wiadomość: nadawca, data, odbiorcy, treść tekstowa (nigdy HTML), załączniki. Starsze zwinięte do zajawki. */
function MessageItem({
  message,
  expanded,
  onToggle,
  now,
  simulated,
}: {
  message: MailMessageView;
  expanded: boolean;
  onToggle: () => void;
  now: Date;
  /** skrzynka w symulacji: wychodząca wiadomość nie wyszła naprawdę */
  simulated: boolean;
}) {
  const recipients = [
    message.to.length ? `do: ${addressesText(message.to)}` : null,
    message.cc.length ? `DW: ${addressesText(message.cc)}` : null,
  ]
    .filter(Boolean)
    .join(' · ');
  const header = (
    <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-body-s">
      <span className="font-medium text-fg">{addressShort(message.from)}</span>
      {message.from.name && <span className="min-w-0 break-all text-fg-muted">{message.from.email}</span>}
      <span className="text-fg-muted">· {formatRelativePast(new Date(message.sentAt), now)}</span>
      {message.direction === 'out' && (
        <Status tone={simulated ? 'warning' : 'neutral'}>{simulated ? 'Wysłana w symulacji' : 'Wysłana'}</Status>
      )}
      <ClassificationStatus message={message} />
    </span>
  );
  if (!expanded) {
    return (
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={false}
        className="flex w-full flex-col gap-1 border-b border-line py-3 text-left hover:bg-surface focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent"
      >
        {header}
        <span className="line-clamp-1 text-body-s text-fg-secondary">{message.bodyText || '(bez treści)'}</span>
      </button>
    );
  }
  return (
    <article className="border-b border-line py-5" aria-label={`Wiadomość od ${addressShort(message.from)}`}>
      <div className="flex items-start gap-2">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          {header}
          {recipients && <span className="break-words text-caption text-fg-muted">{recipients}</span>}
        </div>
        <button
          type="button"
          onClick={onToggle}
          aria-label="Zwiń wiadomość"
          aria-expanded
          className="inline-flex size-7 shrink-0 items-center justify-center rounded-md text-fg-muted hover:bg-elevated focus-visible:outline-2 focus-visible:outline-accent"
        >
          <ChevronDown size={16} strokeWidth={1.75} aria-hidden className="rotate-180" />
        </button>
      </div>
      <div className="mt-4 text-body-m leading-[1.65] break-words whitespace-pre-wrap text-fg">
        {message.bodyText || <span className="text-fg-muted">(bez treści)</span>}
      </div>
      <Attachments message={message} />
    </article>
  );
}

function draftState(d: EmailDraftView): {
  label: string;
  tone: 'neutral' | 'accent' | 'success' | 'danger' | 'warning';
} {
  if (d.manualCheckRequired) return { label: 'Wymaga ręcznej weryfikacji', tone: 'warning' };
  if (d.status === 'pending' && d.approvalState && d.approvalState !== 'pending') {
    return { label: EMAIL_DRAFT_APPROVAL_LABELS[d.approvalState], tone: 'accent' };
  }
  return { label: EMAIL_DRAFT_STATUS_LABELS[d.status], tone: EMAIL_DRAFT_STATUS_TONES[d.status] };
}

function DraftRow({ draft, onEdit }: { draft: EmailDraftView; onEdit: () => void }) {
  const refresh = useMailRefresh();
  const cancel = trpc.mail.cancelDraft.useMutation({
    onSuccess: () => {
      void refresh();
      toast.success('Szkic usunięty');
    },
    onError: (err) => toast.error(errorMessage(err, 'Nie udało się usunąć szkicu.')),
  });
  const confirmNotSent = trpc.mail.confirmNotSent.useMutation({
    onSuccess: () => {
      void refresh();
      toast.success('Oznaczono: mail nie wyszedł. Możesz wysłać go ponownie.');
    },
    onError: (err) => toast.error(errorMessage(err, 'Nie udało się zapisać sprawdzenia.')),
  });
  const state = draftState(draft);
  // jak sprawdzić wysyłkę z nieznanym wynikiem zależy od skrzynki (IMAP: kopii w Wysłanych nie ma — zapytać odbiorcę)
  const sendCheck = MAIL_SEND_CHECK_TEXTS[draft.sendCheck ?? 'sent_folder'];
  return (
    <li className="flex flex-col gap-2 border-b border-line py-3">
      <span className="flex flex-wrap items-center gap-2 text-caption text-fg-muted">
        <StatusPill tone={state.tone} size="s">
          {state.label}
        </StatusPill>
        {draft.createdByType === 'ai' && (
          <span className="inline-flex items-center gap-1">
            <Sparkles size={14} strokeWidth={1.75} aria-hidden className="text-accent" /> Copilot
          </span>
        )}
        <span>{formatRelativePast(new Date(draft.updatedAt))}</span>
      </span>
      <span className="text-body-s font-medium text-fg">{draft.subject || '(bez tematu)'}</span>
      {draft.bodyText && <span className="line-clamp-2 text-body-s text-fg-secondary">{draft.bodyText}</span>}
      {draft.status === 'failed' && draft.lastError && (
        <span className="text-caption text-danger">Nie wysłano: {draft.lastError}</span>
      )}
      {draft.sendOutcomeUnknown && <span className="text-caption text-warning">{sendCheck.hint}</span>}
      <span className="flex flex-wrap items-center gap-2">
        {draft.canEdit && (
          <Button size="s" className={TOUCH_BUTTON} variant="secondary" onClick={onEdit}>
            Edytuj
          </Button>
        )}
        {draft.status === 'pending' && draft.approvalId && (
          <Button size="s" className={TOUCH_BUTTON} variant="ghost" asChild>
            <Link to={`/akceptacje?id=${draft.approvalId}`}>Przejdź do akceptacji</Link>
          </Button>
        )}
        {draft.canEdit && (
          <Button
            size="s"
            className={TOUCH_BUTTON}
            variant="ghost"
            loading={cancel.isPending}
            onClick={() => cancel.mutate({ id: draft.id })}
          >
            Usuń szkic
          </Button>
        )}
        {draft.sendOutcomeUnknown && (
          <Button
            size="s"
            className={TOUCH_BUTTON}
            variant="secondary"
            loading={confirmNotSent.isPending}
            onClick={() => confirmNotSent.mutate({ id: draft.id, version: draft.version })}
          >
            {sendCheck.confirm}
          </Button>
        )}
      </span>
    </li>
  );
}

/**
 * Wątek bez klienta: bursztynowy baner „Nieznany nadawca…” z „Nowy klient” / „Przypisz”. Wątek samych „Mniej ważnych”
 * (newsletter, automat; przegląd 17.10.2026) — spokojna linia z „Przypisz do klienta”, bez koloru i bez „Nowy klient”.
 */
function UnresolvedBanner({
  thread,
  quiet,
  onAssign,
  onCreateClient,
}: {
  thread: MailThreadDetail;
  quiet: boolean;
  onAssign: () => void;
  onCreateClient: () => void;
}) {
  if (!thread.unresolved) return null;
  if (quiet) {
    return (
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-body-s text-fg-muted">
        <span className="inline-flex min-w-0 items-center gap-1.5">
          <UserRound size={14} strokeWidth={1.75} aria-hidden className="shrink-0" />
          <span className="min-w-0">{CLIENT_MATCH_QUIET[thread.unresolved]}</span>
        </span>
        {thread.permissions.canAssign && (
          <Button size="s" className={TOUCH_BUTTON} variant="ghost" onClick={onAssign}>
            {thread.unresolved === 'ambiguous' ? 'Wybierz klienta' : 'Przypisz do klienta'}
          </Button>
        )}
      </div>
    );
  }
  return (
    <div
      role="status"
      className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg bg-warning-subtle px-4 py-2 text-body-s text-fg"
    >
      <UserRound size={16} strokeWidth={1.75} aria-hidden className="shrink-0 text-warning" />
      <span className="min-w-[min(14rem,100%)] flex-1">{CLIENT_MATCH_BANNERS[thread.unresolved]}</span>
      <span className="flex flex-wrap items-center gap-2">
        {thread.unresolved === 'unknown' && thread.permissions.canCreateClient && (
          <Button size="s" className={TOUCH_BUTTON} variant="secondary" onClick={onCreateClient}>
            Nowy klient
          </Button>
        )}
        {thread.permissions.canAssign && (
          <Button size="s" className={TOUCH_BUTTON} variant="ghost" onClick={onAssign}>
            {thread.unresolved === 'ambiguous' ? 'Wybierz klienta' : 'Przypisz'}
          </Button>
        )}
      </span>
    </div>
  );
}

/**
 * Prywatność wątku ze skrzynki osobistej (DECISIONS „Skrzynka osobista jest prywatna”): właściciel widzi „tylko Ty”
 * z „Udostępnij zespołowi” (albo „Cofnij udostępnienie”), inne osoby — kto udostępnił i że odpowiada właściciel.
 * Skrzynka firmowa: nic (widoczność wg zakresu klienta jest domyślna).
 */
function PrivacyRow({ thread }: { thread: MailThreadDetail }) {
  const refresh = useMailRefresh();
  const share = trpc.mail.shareThread.useMutation({
    onSuccess: (res, vars) => {
      void refresh();
      if (vars.shared) {
        toast.success('Udostępniono wątek zespołowi', {
          action: { label: 'Cofnij', onClick: () => share.mutate({ id: res.id, shared: false }) },
        });
      } else {
        toast.success('Wątek znów widzisz tylko Ty');
      }
    },
    onError: (err) => toast.error(errorMessage(err, 'Nie udało się zmienić udostępnienia.')),
  });
  const p = thread.privacy;
  if (!p.personal) return null;
  const Icon = p.shared ? Users : Lock;
  const text = !p.mine
    ? `Wątek ze skrzynki osobistej${p.ownerName ? ` (${p.ownerName})` : ''}, udostępniony zespołowi. Odpowiada właściciel skrzynki.`
    : p.shared
      ? MAIL_MESSAGES.sharedThreadHint
      : MAIL_MESSAGES.privateThreadHint;
  return (
    <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-body-s text-fg-muted">
      <span className="inline-flex min-w-0 items-start gap-1.5">
        <Icon size={14} strokeWidth={1.75} aria-hidden className="mt-[3px] shrink-0" />
        <span className="min-w-0">{text}</span>
      </span>
      {p.canShare && (
        <Button
          size="s"
          className={TOUCH_BUTTON}
          variant="ghost"
          loading={share.isPending}
          onClick={() => share.mutate({ id: thread.id, shared: !p.shared })}
        >
          {p.shared ? 'Cofnij udostępnienie' : 'Udostępnij zespołowi'}
        </Button>
      )}
    </div>
  );
}

type ComposerState = { mode: 'closed' } | { mode: 'reply' } | { mode: 'draft'; draftId: string };

/**
 * Wątek wiadomości (Figma 02 „Detail”: tytuł, nadawca i data, baner „Nieznany nadawca…”, treść, blok AI „Mogę
 * przygotować odpowiedź…”, „Odpowiedz”). Wiadomości tylko tekstem, starsze zwinięte; załączniki zapisane do pobrania,
 * pominięte z powodem; szkice wątku ze stanem akceptacji; przypisanie klienta z wyborem kandydata; „Nowy klient”
 * z nadawcy; baner utraty autoryzacji skrzynki. `actions` = dodatkowe przyciski kompozycji (np. „Utwórz zadanie”),
 * `headerAction` = element obok tytułu (np. menu „…” pozycji Inboxu), `notice` = baner kompozycji pod banerami wątku
 * (np. „Odłożone do …”), `footer` = treść pod akcjami (np. powiązania).
 */
export function ThreadView({
  threadId,
  actions,
  headerAction,
  notice,
  footer,
  className,
  headingLevel = 'h2',
}: {
  threadId: string;
  actions?: ReactNode;
  headerAction?: ReactNode;
  notice?: ReactNode;
  footer?: ReactNode;
  className?: string;
  headingLevel?: 'h1' | 'h2';
}) {
  // NOT_FOUND nie jest ponawiany (domyślne ustawienia klienta zapytań, app/providers.tsx)
  const query = trpc.mail.getThread.useQuery({ id: threadId });
  const refresh = useMailRefresh();
  const [composer, setComposer] = useState<ComposerState>({ mode: 'closed' });
  const [assignOpen, setAssignOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const prepare = trpc.mail.draftReplyWithAi.useMutation({
    onSuccess: (res) => {
      void refresh();
      if (res.state === 'ok' && res.draft) {
        if (res.existing) toast.info(res.message ?? 'Otwieram istniejący szkic AI.');
        setComposer({ mode: 'draft', draftId: res.draft.id });
      } else {
        toast.info(res.message ?? 'Copilot nie przygotował odpowiedzi. Odpowiedz ręcznie.');
      }
    },
    onError: (err) => toast.error(errorMessage(err, 'Nie udało się przygotować odpowiedzi.')),
  });

  if (query.isPending) return <ThreadSkeleton />;
  if (query.isError) {
    return appErrorCode(query.error) === 'NOT_FOUND' ? (
      <EmptyState
        icon={Mail}
        title="Nie ma tego wątku"
        description="Mógł zostać przeniesiony do klienta spoza Twojego zakresu albo link jest nieaktualny."
      />
    ) : (
      <ErrorState onRetry={() => void query.refetch()} retrying={query.isFetching} />
    );
  }

  const t = query.data;
  const now = new Date();
  const last = t.messages[t.messages.length - 1];
  const Heading = headingLevel;
  // akcje kompozycji (np. „Utwórz zadanie” z wiadomości) — slot `setThreadActions`
  const ThreadActions = getThreadActions();
  const isExpanded = (m: MailMessageView) => expanded[m.id] ?? m.id === last?.id;
  // same „Mniej ważne” odebrane (newsletter, automat): bez bursztynowego banera i propozycji odpowiedzi AI
  const incoming = t.messages.filter((m) => m.direction === 'in');
  const quiet = incoming.length > 0 && incoming.every((m) => m.importance === 'less');
  // szkic AI do ostatniej wiadomości: „Otwórz szkic” zamiast kolejnego „Przygotuj” (jeden szkic, jedno zadanie AI)
  const aiDraft =
    last?.direction === 'in'
      ? [...t.drafts]
          .reverse()
          .find(
            (d) =>
              d.createdByType === 'ai' &&
              (d.status === 'draft' || d.status === 'pending') &&
              d.replyToMessageId === last.id,
          )
      : undefined;

  return (
    <article aria-labelledby={`thread-${t.id}`} className={cn('flex flex-col', className)}>
      {headerAction ? (
        <div className="flex items-start gap-2">
          <Heading id={`thread-${t.id}`} className="min-w-0 flex-1 text-h3 break-words text-fg">
            {t.subject}
          </Heading>
          {headerAction}
        </div>
      ) : (
        <Heading id={`thread-${t.id}`} className="text-h3 break-words text-fg">
          {t.subject}
        </Heading>
      )}
      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-body-s text-fg-muted">
        {t.client ? (
          <Link
            to={`/klienci/${t.client.id}`}
            className="rounded-xs font-medium text-fg-secondary underline-offset-2 hover:text-fg hover:underline focus-visible:outline-2 focus-visible:outline-accent"
          >
            {t.client.name}
          </Link>
        ) : (
          <span>Bez klienta</span>
        )}
        <span>
          {t.messages.length === 1 ? '1 wiadomość' : `${t.messages.length} wiadomości`} · {t.mailbox.address}
        </span>
        {t.simulated && <Status tone="warning">Symulacja</Status>}
      </div>
      <PrivacyRow thread={t} />

      <div className="mt-5 flex flex-col gap-3 empty:hidden">
        <MailboxBanner mailbox={t.mailbox} />
        <UnresolvedBanner
          thread={t}
          quiet={quiet}
          onAssign={() => setAssignOpen(true)}
          onCreateClient={() => setCreateOpen(true)}
        />
        {notice}
      </div>

      <div className="mt-3 flex flex-col">
        {t.messages.map((m) => (
          <MessageItem
            key={m.id}
            message={m}
            now={now}
            simulated={t.simulated}
            expanded={isExpanded(m)}
            onToggle={() => setExpanded((prev) => ({ ...prev, [m.id]: !isExpanded(m) }))}
          />
        ))}
      </div>

      {t.drafts.length > 0 && (
        <section aria-label="Szkice odpowiedzi" className="mt-6">
          <h3 className="text-caption font-medium text-fg-muted">Szkice</h3>
          <ul className="mt-1 flex flex-col">
            {t.drafts.map((d) => (
              <DraftRow key={d.id} draft={d} onEdit={() => setComposer({ mode: 'draft', draftId: d.id })} />
            ))}
          </ul>
        </section>
      )}

      {t.ai.available && t.reply && last?.direction === 'in' && !quiet && (
        // Figma 02 „Detail”: jedna linia — tytuł, „Trafi do Akceptacji…”, „Przygotuj” po prawej; tylko gdy ostatnia
        // wiadomość jest od klienta (po naszej odpowiedzi nie ma na co odpowiadać)
        <section
          aria-label="Copilot"
          className="mt-6 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl bg-surface px-4 py-3"
        >
          <Sparkles size={16} strokeWidth={1.75} aria-hidden className="shrink-0 text-accent" />
          <div className="flex min-w-0 flex-1 flex-col">
            <h3 className="text-body-s font-medium text-fg">
              {aiDraft ? 'Szkic odpowiedzi AI jest gotowy' : 'Mogę przygotować odpowiedź'}
            </h3>
            <p className="text-caption text-fg-muted">Trafi do Akceptacji, nic nie wyślę sam.</p>
          </div>
          {aiDraft ? (
            <>
              <Button
                size="s"
                className={TOUCH_BUTTON}
                variant="ghost"
                loading={prepare.isPending}
                disabled={aiDraft.sendOutcomeUnknown}
                onClick={() => prepare.mutate({ threadId: t.id, regenerate: true })}
              >
                Przygotuj nowy
              </Button>
              <Button
                size="s"
                className={TOUCH_BUTTON}
                variant="secondary"
                onClick={() => setComposer({ mode: 'draft', draftId: aiDraft.id })}
              >
                Otwórz szkic
              </Button>
            </>
          ) : (
            <Button
              size="s"
              className={TOUCH_BUTTON}
              variant="secondary"
              loading={prepare.isPending}
              onClick={() => prepare.mutate({ threadId: t.id })}
            >
              Przygotuj
            </Button>
          )}
        </section>
      )}

      {(t.permissions.canReply && t.reply) || actions || ThreadActions ? (
        <div className="mt-6 flex flex-wrap items-center gap-2">
          {t.permissions.canReply && t.reply && (
            <Button
              variant={quiet ? 'secondary' : 'primary'}
              icon={Reply}
              onClick={() => setComposer({ mode: 'reply' })}
            >
              Odpowiedz
            </Button>
          )}
          {ThreadActions && <ThreadActions thread={t} />}
          {actions}
        </div>
      ) : null}

      {footer}

      <Composer
        open={composer.mode !== 'closed'}
        onOpenChange={(open) => !open && setComposer({ mode: 'closed' })}
        draftId={composer.mode === 'draft' ? composer.draftId : null}
        threadId={t.id}
        replyToMessageId={t.reply?.replyToMessageId ?? null}
        clientId={t.client?.id ?? null}
        defaults={t.reply ? { to: t.reply.to, cc: t.reply.cc, subject: t.reply.subject, bodyText: '' } : undefined}
        title={
          composer.mode === 'draft'
            ? t.drafts.find((d) => d.id === composer.draftId)?.createdByType === 'ai'
              ? 'Szkic odpowiedzi AI'
              : 'Szkic odpowiedzi'
            : 'Odpowiedź'
        }
      />
      {t.permissions.canAssign && <AssignClientDialog open={assignOpen} onOpenChange={setAssignOpen} thread={t} />}
      {t.permissions.canCreateClient && (
        <CreateClientFromSenderDialog open={createOpen} onOpenChange={setCreateOpen} thread={t} />
      )}
    </article>
  );
}
